// Default workspace names for a tab that opens several files at once. One file keeps its own
// name (the Worker does that when the tab sends none).

/** A shared text shorter than this (after trimming) is not worth showing. */
const MIN_COMMON_LENGTH = 6;
/** The name is cut at 120 characters by the Worker; keep the label short so the time survives. */
const MAX_LABEL_LENGTH = 60;
const SEPARATORS = /^[-_.\s]+|[-_.\s]+$/g;

/** Local time as YYYYMMDD-HHMMSS. */
export function timestampName(date = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${p(date.getMonth() + 1)}${p(date.getDate())}-${p(date.getHours())}${p(date.getMinutes())}${p(date.getSeconds())}`;
}

/** Drop the characters a file name cannot hold. */
export function cleanLabel(text) {
  return String(text ?? "")
    .replace(/[\u0000-\u001f\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_LABEL_LENGTH)
    .trim();
}

/**
 * The text that most of the file names have in common: the one found in the most files, and
 * among those the longest. `-`, `_`, `.` and spaces at its ends are cut off, and the file
 * extension is ignored. Returns "" when nothing of at least MIN_COMMON_LENGTH characters is
 * shared by two files.
 */
export function commonText(fileNames) {
  const texts = fileNames.map((n) => String(n).replace(/\.[^.\\/]{1,10}$/, ""));
  if (texts.length < 2) return "";

  // A generalized suffix automaton over all the names. `files[v]` counts the names in which
  // the strings of state v occur; `where[v]` is one place such a string ends.
  const next = [new Map()];
  const link = [-1];
  const len = [0];
  const where = [null];
  const extend = (last, ch, file, end) => {
    const hit = next[last].get(ch);
    if (hit !== undefined) {
      if (len[hit] === len[last] + 1) return hit;
      const clone = next.length;
      next.push(new Map(next[hit]));
      link.push(link[hit]);
      len.push(len[last] + 1);
      where.push(where[hit]);
      link[hit] = clone;
      for (let p = last; p !== -1 && next[p].get(ch) === hit; p = link[p]) next[p].set(ch, clone);
      return clone;
    }
    const cur = next.length;
    next.push(new Map());
    link.push(0);
    len.push(len[last] + 1);
    where.push([file, end]);
    let p = last;
    while (p !== -1 && !next[p].has(ch)) {
      next[p].set(ch, cur);
      p = link[p];
    }
    if (p !== -1) {
      const q = next[p].get(ch);
      if (len[q] === len[p] + 1) {
        link[cur] = q;
      } else {
        const clone = next.length;
        next.push(new Map(next[q]));
        link.push(link[q]);
        len.push(len[p] + 1);
        where.push(where[q]);
        link[q] = clone;
        link[cur] = clone;
        for (; p !== -1 && next[p].get(ch) === q; p = link[p]) next[p].set(ch, clone);
      }
    }
    return cur;
  };
  texts.forEach((text, file) => {
    let last = 0;
    for (let i = 0; i < text.length; i++) last = extend(last, text[i], file, i + 1);
  });

  const files = new Array(next.length).fill(0);
  const seen = new Array(next.length).fill(-1);
  texts.forEach((text, file) => {
    let state = 0;
    for (let i = 0; i < text.length; i++) {
      state = next[state].get(text[i]);
      for (let v = state; v > 0 && seen[v] !== file; v = link[v]) {
        seen[v] = file;
        files[v]++;
      }
    }
  });

  let best = "";
  let bestFiles = 1;
  for (let v = 1; v < next.length; v++) {
    if (files[v] < 2 || len[v] < MIN_COMMON_LENGTH || files[v] < bestFiles) continue;
    const [file, end] = where[v];
    const text = texts[file].slice(end - len[v], end).replace(SEPARATORS, "");
    if (text.length < MIN_COMMON_LENGTH) continue;
    if (files[v] > bestFiles || text.length > best.length) {
      best = text;
      bestFiles = files[v];
    }
  }
  return best;
}

/**
 * The name for a workspace opened from several files, e.g. `6 files assets-_mx-spinelobbies (20251022-091800)`.
 * With one file this returns undefined, so the Worker names it after the file.
 * `label` is a ready-made part for the middle (Game Files passes the search text); without
 * one, the part the file names share is used, when there is any.
 */
export function workspaceName(fileNames, { game = false, label, date = new Date() } = {}) {
  if (fileNames.length < 2) return undefined;
  const middle = cleanLabel(label ?? (game ? "" : commonText(fileNames)));
  return [`${fileNames.length} ${game ? "game files" : "files"}`, middle, `(${timestampName(date)})`].filter(Boolean).join(" ");
}
